import { Formik } from 'formik';
import { camelCase, pick } from 'lodash';
import * as R from 'ramda';
import React, { useMemo, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Link, useParams, generatePath, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import Page from 'App/components/Page/Page';
import useFetchActivityDetails from 'App/hooks/useFetchActivityDetails';
import useFetchPatient from 'App/hooks/useFetchPatient';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import useUpdateReferralActivityMutation from 'App/hooks/useUpdateReferralActivityMutation';
import useUpdateReferralActivityMedicationMutation from 'App/hooks/useUpdateReferralActivityMedicationMutation';
import { errorHandler } from 'App/utils';
import { patientRoute, referralDetailRoute } from 'App/common/routeLookup';
import toast from 'Shared/toast';
import DeleteModal from 'App/components/DeleteModal/DeleteModal';
import useDeleteReferralActivity from 'App/hooks/useDeleteReferralActivity';
import {
    computePatientInsurance,
    convertBiInsuranceToView,
    convertBItoSave,
    convertBItoView,
    convertInsuranceToView,
} from '../referral.utils';
import InformationForm from './components/InformationForm';
import MedicationTableForm from './components/MedicationTableForm';
import {
    addReferralActivityBiPlan,
    createReferralActivityNote,
    deleteReferralActivityBiPlan,
} from 'App/services/RMSService';
import PESService from 'App/services/PESService';
import useFetchPatientInsurance from 'App/hooks/useFetchPatientInsurance';
import SubmitButton from 'Shared/form/SubmitButton';
import PatientHeader from 'App/components/PatientHeader';
import BenefitsInvestigationInsuranceForm from './components/BenefitsInvestigationInsuranceForm';
import useFetchPatientBIInsurance from 'App/hooks/useFetchPatientBIInsurance';
import useFetchEhrBranchSettings from 'App/hooks/useFetchEhrBranchSettings';
import usePatientEhr from 'App/components/PatientProfile/hooks/usePatientEhr';
import EhrEncounterNoteModal from 'App/components/EHR/EhrEncounterNoteModal';
import BenefitsInvestigationEncounterReviewContent from './components/BenefitsInvestigationModalReview';
import { BI_STATUS_TYPE } from 'App/features/ReferralManager/Referral/PriorAuthorization/constant';
import ReferralActivityHistory from 'App/components/ReferralActivityHistory';
import { queryClient } from 'Shared/queryClient';
import EhrProviderNotFoundSection from 'App/components/Assessments/ehr/EhrProviderNotFound';
import { useUserContext } from 'App/contexts/UserContext';
import useFetchRequiredPatientFields from 'App/hooks/useFetchRequiredPatientFields';
import useFetchDisabledPatientFields from 'App/hooks/useFetchDisabledPatientFields';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';

const propTypes = {};
const defaultProps = {};

const validationSchema = yup.object().shape({
    status: yup.string().required().label('Status'),
    createdOn: yup.date().nullable().label('Start Date'),
    dueOn: yup.date().nullable().label('Due Date'),
    details: yup.object().shape({
        completedOn: yup.date().nullable().label('Complete Date'),
        notes: yup.string().max(255).label('Notes'),
    }),
    patientInsurance: yup.array().of(
        yup.object().shape({
            name: yup.string().required('Plan name is required').max(100, 'Plan name cannot exceed 100 characters'),
        })
    ),
});

function BenefitsInvestigation() {
    const { patientId, referralId, benefitsInvestigationId, typeId } = useParams();
    const { ehr, isEhrLoading } = usePatientEhr(patientId);
    const navigate = useNavigate();
    const { data: requiredFields = {} } = useFetchRequiredPatientFields();
    const { data: disabledFields = {} } = useFetchDisabledPatientFields();
    const isInsuranceRequired = !!Object.values(requiredFields).find(
        (field) => camelCase(field.field) === 'referralBiInsurancePlans'
    );
    const isInsuranceDisabled = !!Object.values(disabledFields).find(
        (field) => camelCase(field.field) === 'referralBiInsurancePlans'
    );
    const { isLoading: isLoadingPatient, isError, data: patient } = useFetchPatient(patientId);
    const { data: activityData, isLoading: isActivityLoading } = useFetchActivityDetails(
        patientId,
        referralId,
        benefitsInvestigationId,
        typeId
    );
    const updateReferralActivity = useUpdateReferralActivityMutation();
    const updateReferralActivityMedication = useUpdateReferralActivityMedicationMutation();
    const navigateToReferralDetailPage = () => {
        navigate(generatePath(referralDetailRoute, { patientId, referralId }));
    };
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [ehrModalOpen, setEhrModalOpen] = useState(false);
    const deleteReferralActivity = useDeleteReferralActivity();
    const fetchPatientInsuranceQuery = useFetchPatientInsurance(patientId);
    const { data: biInsurance, isLoading: isInsuranceLoading } = useFetchPatientBIInsurance(
        patientId,
        Number(referralId),
        Number(benefitsInvestigationId)
    );

    const activity = useMemo(() => {
        if (!isActivityLoading && activityData) {
            return convertBItoView(activityData);
        }
    }, [activityData, isActivityLoading]);

    const insurance = useMemo(() => {
        if (fetchPatientInsuranceQuery.isSuccess) {
            return R.values(fetchPatientInsuranceQuery.data);
        }
        return [];
    }, [fetchPatientInsuranceQuery.data, fetchPatientInsuranceQuery.isSuccess]);

    const { data: branchSettingsData, isLoading: isBranchSettingsLoading } = useFetchEhrBranchSettings();

    let displayBenefitsInvestigationEncounterModal = false;
    const user = useUserContext();
    const userEhrEligible = user?.user_attributes?.ehrProviderId;
    if (!isBranchSettingsLoading && !isEhrLoading) {
        const { redoxRMDestinationId, redoxRMDestinationName, rmNoteDocumentType } = branchSettingsData;
        displayBenefitsInvestigationEncounterModal =
            ehr?.patientSynced &&
            redoxRMDestinationId &&
            redoxRMDestinationName &&
            rmNoteDocumentType &&
            userEhrEligible;
    }

    const isLoading = R.anyPass([
        R.always(isLoadingPatient),
        R.always(isInsuranceLoading),
        R.always(fetchPatientInsuranceQuery.isLoading),
        R.always(isActivityLoading),
    ])();

    const disableInputs = useMemo(() => {
        return R.anyPass([
            R.always(isLoading),
            R.pathEq(BI_STATUS_TYPE.DELETED, ['status', 'id']),
            R.pathEq(BI_STATUS_TYPE.COMPLETED, ['status', 'id']),
        ])(activity);
    }, [activity, isLoading]);

    const disableSendToEhrButton = useMemo(() => {
        const completedStatuses = [BI_STATUS_TYPE.COMPLETED, BI_STATUS_TYPE.DELETED];
        return R.anyPass([
            R.always(isLoading),
            R.pipe(
                R.propOr([], 'history'),
                R.any((item) => R.includes(item.referralActivityStatus, completedStatuses))
            ),
        ])(activityData);
    }, [activityData, isLoading]);

    const initialValues = useMemo(() => {
        const formattedInsurance = convertInsuranceToView(insurance);
        const formattedBiInsurance = convertBiInsuranceToView(biInsurance);

        const { allActiveInsurance, patientInsurance } = computePatientInsurance(
            formattedInsurance,
            formattedBiInsurance
        );

        return {
            ...(activity || {}),
            disableInputs,
            allActiveInsurance,
            patientInsurance,
            disableSendToEhrButton,
        };
    }, [activity, biInsurance, disableInputs, insurance, disableSendToEhrButton]);

    const deleteActivity = () => {
        deleteReferralActivity
            .mutateAsync({
                patientId,
                referralId,
                activityId: benefitsInvestigationId,
            })
            .then(() => {
                navigateToReferralDetailPage();
            })
            .catch(errorHandler);
    };

    const invalidateInsuranceQueries = () => {
        queryClient.invalidateQueries(['patient', patientId, 'biInsurance']);
        queryClient.invalidateQueries(['patient', patientId, 'insurance']);
    };

    const updatePatientInsurance = (insurancePayload) => {
        if (insurancePayload.length > 0) {
            const promiseArray = insurancePayload.map((item) => {
                // Insurance already exist
                if (item.id) {
                    // this insurance was already assigned to BI plan ( this is a new patient insurance)
                    if (item.uuid) {
                        return PESService.updatePatientInsurance(patientId, item.id, item);
                    }
                    // The else part is no more required
                    // else (for old patient insurance select from the dropdown)
                    // -> Update the BI plan insurance (RMS old API) (rmsService.updateInsurancePlan)
                    // -> Create a new patient insurance ( we get the uuid here)
                    // -> update the patient uuid (RMS old API) (rmsService.updateInsurancePlan)
                }

                // New Insurance
                // -> save the patient insurance -> get the UUID
                // -> Add it to the BI plan using UUID
                let patientInsuranceUuids = [];
                return PESService.addPatientInsurance(patientId, {
                    insurance: [item],
                })
                    .then((res) => {
                        const insuranceKeys = Object.keys(res);
                        patientInsuranceUuids = insuranceKeys.map((it) => {
                            return res[it].uuid;
                        });
                    })
                    .then(() => {
                        return addReferralActivityBiPlan(
                            patientId,
                            patientInsuranceUuids,
                            parseInt(referralId, 10),
                            parseInt(benefitsInvestigationId, 10)
                        );
                    });
            });
            return Promise.all(promiseArray).then(invalidateInsuranceQueries);
        }
    };

    const removeBIInsurance = (insuranceToRemove) => {
        if (insuranceToRemove.length) {
            const referralActivityBiPlanPromises = insuranceToRemove.map((item) =>
                deleteReferralActivityBiPlan(
                    patientId,
                    Number(referralId),
                    Number(benefitsInvestigationId),
                    item.patientInsuranceUuid
                )
            );

            return Promise.all(referralActivityBiPlanPromises).then(invalidateInsuranceQueries);
        }
        return Promise.resolve();
    };

    const openEhrEncounterNoteModal = () => {
        setEhrModalOpen(true);
    };

    return (
        <Page
            title="Benefits Investigation"
            permissionType={PERMISSION_TYPES.ENABLE_REFERRAL}
            isLoading={isLoading}
            showNotFound={isError}
        >
            {isLoading && !isError && patient ? null : (
                <>
                    <div className="container page-wrapper">
                        <Row>
                            <Col md={12}>
                                <PatientHeader patient={patient} showEhrStatus={false} />
                            </Col>
                        </Row>
                        <nav className="profile-actions">
                            <ul className="list-inline">
                                <li className="list-inline-item">
                                    <Link to={generatePath(patientRoute, { patientId })}>Back to Patient Profile</Link>
                                </li>
                                <li className="list-inline-item">
                                    <Link to={generatePath(referralDetailRoute, { patientId, referralId })}>
                                        Back to Referral
                                    </Link>
                                </li>
                            </ul>
                        </nav>

                        {displayBenefitsInvestigationEncounterModal && (
                            <div>
                                <ReferralActivityHistory activityHistory={activityData?.history} />
                            </div>
                        )}

                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            validate={(values) => {
                                const errors = {};
                                if (isInsuranceRequired && !isInsuranceDisabled && !values.patientInsurance?.length) {
                                    errors.patientInsurance = 'At least one insurance plan is required.';
                                }
                                return errors;
                            }}
                            onSubmit={(values) => {
                                const formattedInsurance = convertInsuranceToView(insurance);
                                const {
                                    medicationPayload,
                                    notesPayload,
                                    insurancePayload,
                                    activityPayload,
                                    removeInsurancePayload,
                                } = convertBItoSave(values, formattedInsurance, biInsurance);
                                const medicationPromiseArray = medicationPayload.map((med) =>
                                    updateReferralActivityMedication.mutateAsync({
                                        patientId,
                                        referralId,
                                        activityId: benefitsInvestigationId,
                                        medicationId: med.id,
                                        data: pick(med, ['medication_id', 'data']),
                                    })
                                );
                                return Promise.all(medicationPromiseArray)
                                    .then(() => createReferralActivityNote(benefitsInvestigationId, notesPayload))
                                    .then(() => updatePatientInsurance(insurancePayload))
                                    .then(() => removeBIInsurance(removeInsurancePayload))
                                    .then(() =>
                                        updateReferralActivity.mutateAsync({
                                            patientId,
                                            referralId,
                                            activityId: benefitsInvestigationId,
                                            data: activityPayload,
                                        })
                                    )
                                    .then(() => {
                                        toast.success('Activity Information has been updated successfully');
                                        if (displayBenefitsInvestigationEncounterModal) {
                                            openEhrEncounterNoteModal();
                                        } else {
                                            navigateToReferralDetailPage();
                                        }
                                    })
                                    .catch(errorHandler);
                            }}
                        >
                            {({ isSubmitting, handleSubmit, values }) => {
                                return (
                                    <form onSubmit={handleSubmit} noValidate>
                                        <InformationForm />
                                        <MedicationTableForm />
                                        <BenefitsInvestigationInsuranceForm />
                                        {!userEhrEligible && !isEhrLoading && ehr?.patientSynced && (
                                            <EhrProviderNotFoundSection />
                                        )}
                                        <FormButtonGroup>
                                            <Button
                                                variant="danger"
                                                onClick={() => setShowDeleteModal(true)}
                                                disabled={isSubmitting || values.disableInputs}
                                            >
                                                Delete
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                disabled={isSubmitting}
                                                onClick={navigateToReferralDetailPage}
                                            >
                                                Cancel
                                            </Button>
                                            <SubmitButton disabled={values?.disableInputs}>Save Activity</SubmitButton>
                                            {displayBenefitsInvestigationEncounterModal &&
                                                initialValues.status?.id === BI_STATUS_TYPE.COMPLETED && (
                                                    <Button
                                                        variant="outline-secondary"
                                                        disabled={isSubmitting || values.disableSendToEhrButton}
                                                        onClick={openEhrEncounterNoteModal}
                                                    >
                                                        Send To EHR
                                                    </Button>
                                                )}
                                        </FormButtonGroup>

                                        {ehrModalOpen &&
                                            !isActivityLoading &&
                                            !isBranchSettingsLoading &&
                                            values.status?.id &&
                                            !isEhrLoading && (
                                                <EhrEncounterNoteModal
                                                    activity={{ ...activityData }}
                                                    patient={patient}
                                                    branchSettings={branchSettingsData}
                                                    activityStatus={values.status.id}
                                                    onComplete={() => {
                                                        return navigateToReferralDetailPage();
                                                    }}
                                                >
                                                    <BenefitsInvestigationEncounterReviewContent
                                                        patient={patient}
                                                        branchSettings={branchSettingsData}
                                                        medicationList={values.medications}
                                                        benefitsInvestigationReferralDetails={{
                                                            status: values.status?.id,
                                                            startDate: values.createdOn,
                                                            endDate: values.dueOn,
                                                            patientTotalResponsibility:
                                                                values.details?.patientTotalResponsibilityAmount,
                                                            statusReason: values.details?.reasonCode,
                                                            initialMedicationSite: values.details?.initialMedSiteOfCare,
                                                            activityNotes: values.details?.notes,
                                                            completeDate: values.details?.completeDate,
                                                        }}
                                                    />
                                                </EhrEncounterNoteModal>
                                            )}
                                    </form>
                                );
                            }}
                        </Formik>
                    </div>
                    {showDeleteModal && (
                        <DeleteModal
                            onHide={() => setShowDeleteModal(false)}
                            show={showDeleteModal}
                            onDelete={deleteActivity}
                            title="Delete Activity"
                            message="Are you sure you want to delete this activity?"
                        />
                    )}
                </>
            )}
        </Page>
    );
}

BenefitsInvestigation.propTypes = propTypes;
BenefitsInvestigation.defaultProps = defaultProps;

export default BenefitsInvestigation;
